import React, { useState, useEffect } from 'react';
import { Image as ImageIcon, Check, Loader, Trash2 } from 'lucide-react';
import theme from '../theme';
import MediaThumb from './MediaThumb';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NFTGallery = ({ onSelect, selectedIds = [], allowMultiple = true }) => {
  const [loading, setLoading] = useState(true);
  const [nfts, setNfts] = useState([]);
  // Batch 1B recovery — reactive selection so wizard hydration propagates
  // in (previous version froze the initial selection at mount).
  const [selected, setSelected] = useState(selectedIds);
  useEffect(() => {
    setSelected((prev) => {
      const a = Array.isArray(selectedIds) ? selectedIds : [];
      if (prev.length === a.length && prev.every((v, i) => v === a[i])) return prev;
      return a;
    });
  }, [selectedIds]);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${BACKEND_URL}/api/nft/gallery`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setNfts(data.nfts);
      }
    } catch (error) {
      console.error('Gallery fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (fileId) => {
    if (allowMultiple) {
      const newSelected = selected.includes(fileId)
        ? selected.filter((id) => id !== fileId)
        : [...selected, fileId];
      setSelected(newSelected);
      if (onSelect) {
        onSelect(newSelected);
      }
    } else {
      setSelected([fileId]);
      if (onSelect) {
        onSelect([fileId]);
      }
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this NFT from your gallery?')) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/nft/${fileId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setNfts((prev) => prev.filter((nft) => nft.id !== fileId));
        setSelected((prev) => prev.filter((id) => id !== fileId));
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          padding: '40px',
          textAlign: 'center',
          color: theme.colors.text.secondary,
        }}
      >
        <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ marginTop: '16px' }}>Loading gallery...</p>
      </div>
    );
  }

  if (nfts.length === 0) {
    return (
      <div
        style={{
          padding: '40px',
          textAlign: 'center',
          background: theme.colors.bg.secondary,
          borderRadius: theme.radius.md,
          border: `1px solid ${theme.colors.borders}`,
        }}
      >
        <ImageIcon
          size={48}
          style={{ margin: '0 auto 16px', color: theme.colors.text.tertiary }}
        />
        <p
          style={{
            fontSize: '16px',
            fontWeight: 600,
            color: theme.colors.text.primary,
            marginBottom: '8px',
          }}
        >
          Your gallery is empty
        </p>
        <p style={{ fontSize: '14px', color: theme.colors.text.secondary }}>
          Upload or generate NFTs to get started
        </p>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h4
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: theme.colors.text.primary,
          }}
        >
          Your Artwork Library ({nfts.length})
        </h4>
        {selected.length > 0 && (
          <span
            style={{
              fontSize: '13px',
              color: theme.colors.primary,
              fontWeight: 600,
            }}
          >
            {selected.length} selected
          </span>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: '12px',
        }}
      >
        {nfts.map((nft) => {
          const isSelected = selected.includes(nft.id);
          return (
            <div
              key={nft.id}
              onClick={() => toggleSelect(nft.id)}
              style={{
                position: 'relative',
                border: `2px solid ${
                  isSelected ? theme.colors.primary : theme.colors.borders
                }`,
                borderRadius: theme.radius.sm,
                padding: '8px',
                background: isSelected
                  ? `${theme.colors.primary}15`
                  : theme.colors.bg.card,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {isSelected && (
                <div
                  style={{
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    background: theme.colors.primary,
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                  }}
                >
                  <Check size={16} color="#FFFFFF" />
                </div>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(nft.id);
                }}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'rgba(0,0,0,0.7)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#FFFFFF',
                  zIndex: 1,
                }}
              >
                <Trash2 size={14} />
              </button>

              <MediaThumb
                id={nft.id}
                url={nft.url || `/api/nft/file/${nft.id}`}
                filename={nft.filename}
                kind={nft.kind}
                style={{
                  width: '100%',
                  aspectRatio: '1',
                  objectFit: 'cover',
                  borderRadius: theme.radius.sm,
                  marginBottom: '8px',
                }}
              />

              <p
                style={{
                  fontSize: '11px',
                  color: theme.colors.text.secondary,
                  textAlign: 'center',
                  wordBreak: 'break-word',
                }}
              >
                {nft.filename}
              </p>

              {nft.metadata?.prompt && (
                <p
                  style={{
                    fontSize: '10px',
                    color: theme.colors.text.tertiary,
                    marginTop: '4px',
                    fontStyle: 'italic',
                  }}
                >
                  AI Generated
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NFTGallery;
